import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { Alert, AutocompleteComplex, Button, Input } from '@atoms';
import { PERMISSIONS } from '@globalConstants';
import { useAddClient, useCustomerGroups, usePermissions } from '@hooks';
import { BoxContainer, FullWidthContent, MainMenuLayout } from '@layouts';
import { AutocompleteComplexFetch, SelectFetch } from '@molecules';
import { CUSTOMER_TYPES, FIELDS, FIELDS_ADDRESS, validationSchema } from './constants';
import styles from './styles';
/**
 * Renders a modal with the inputs fields and search clients
 * @param {object} props
 * @param  {boolean} props.isOpen is modal is or not visible
 * @param  {() => void | () => Promise<void>} props.onClose function to execuete after modal close
 * @param  {object} props.params the current URL params
 * @param  {() => void | () => Promise<void>} props.onFiltersFormSubmit function to set clients data
 */
const AddClient = () => {
  const { t } = useTranslation();
  const { addClient, isError, setIsError, alertMessage, isSuccess, setIsSuccess } = useAddClient();
  const isDemo = usePermissions([PERMISSIONS.demoCreateClient]);
  const responsive = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const {
    data,
    isLoading: isLoadingCustomerGroups,
    isError: groupsError,
    setIsError: setGroupsError,
  } = useCustomerGroups();

  const {
    handleSubmit,
    handleChange,
    isValid,
    errors,
    values,
    onBlur,
    resetForm,
    isSubmitting: isLoading,
  } = useFormik({
    initialValues: {
      cuit: '',
      companyName: '',
      companyGroup: '',
      segment: '',
      customerType: null,
      province: '',
      city: '',
      street: '',
      number: '',
      floor: '',
      apartment: '',
      postalCode: '',
    },
    validationSchema,
    enableReinitialize: true,
    validateOnMount: false,
    onSubmit: async (values, { resetForm }) => {
      const createdClient = await addClient(values);
      if (!createdClient) {
        return;
      }
      resetForm({
        values: {
          cuit: '',
          companyName: '',
          companyGroup: '',
          segment: '',
          customerType: null,
          province: '',
          city: null,
          street: '',
          number: '',
          floor: '',
          apartment: '',
          postalCode: '',
        },
      });
    },
  });

  /**
   * Resets the form values and close modal
   */
  const handleCancel = () => {
    resetForm({
      values: {
        cuit: '',
        companyName: '',
        companyGroup: null,
        segment: '',
        customerType: null,
        province: '',
        city: null,
        street: '',
        number: '',
        floor: '',
        apartment: '',
        postalCode: '',
      },
    });
  };

  const disableButton =
    !(values.cuit && values.companyName && values.segment) || !isValid || isDemo;
  return (
    <MainMenuLayout>
      <FullWidthContent>
        <BoxContainer>
          <Typography variant='h4'>{t('CLIENTS:ADD_CLIENT')}</Typography>
          <Box style={styles.inputsContainer(responsive)}>
            <FormFields
              {...{
                fields: FIELDS,
                values,
                errors,
                handleChange,
                onBlur,
                responsive,
                data,
                isLoadingCustomerGroups,
                groupsError,
              }}
            />
          </Box>
          <Typography variant='h4'>{t('CLIENTS:ADDRESS')}</Typography>
          <Box style={styles.inputsContainer(responsive)}>
            <FormFields
              {...{
                fields: FIELDS_ADDRESS,
                values,
                errors,
                handleChange,
                onBlur,
                responsive,
                data,
                isLoadingCustomerGroups,
                groupsError,
              }}
            />
          </Box>
          <Box sx={styles.buttonsBox}>
            <Button
              text={t('COMMON:CLEAR_FORM')}
              onClick={handleCancel}
              customStyle={styles.clearButton}
            />
            <Button
              variant='contained'
              color='primary'
              type='submit'
              isLoading={isLoading}
              disabled={disableButton}
              onClick={handleSubmit}
              text={t('COMMON:CONFIRM')}
            />
          </Box>
          <Alert
            modalVisible={isError || groupsError}
            handleClose={() => {
              setIsError(false);
              setGroupsError(false);
            }}
            variant='error'
            title={alertMessage.title}
            description={alertMessage.description}
            details={alertMessage.details}
          />
          <Alert
            modalVisible={isSuccess}
            handleClose={() => setIsSuccess(false)}
            variant='success'
            title={'CLIENTS:CREATE_CLIENT:SUCCESS_TITLE'}
            description={alertMessage.description}
            details={alertMessage.details}
          />
        </BoxContainer>
      </FullWidthContent>
    </MainMenuLayout>
  );
};
/**
 * Fucntion to generate fields from array of configs
 * @param props
 * @returns
 */
const FormFields = ({
  fields,
  values,
  errors,
  handleChange,
  onBlur,
  responsive,
  data,
  isLoadingCustomerGroups,
  groupsError,
}) => {
  return fields?.map(({ name, label, type, getOptions, disabled, parseOption }) => {
    const error = Boolean(name in values && errors[name]);
    const helperText = error ? errors[name] : '';

    return type === 'input' ? (
      <Input
        key={name}
        label={label}
        variant='outlined'
        size='small'
        name={name}
        value={values[name]}
        onChange={handleChange(name)}
        inputProps={{ onPaste: handleChange(name) }}
        onBlur={onBlur}
        helperText={helperText}
        error={error}
        sx={styles.inputText(responsive)}
        fullWidth
      />
    ) : type === 'selectFetch' ? (
      <Box sx={styles.inputText(responsive)} key={name}>
        <SelectFetch
          onChange={(item) => handleChange(name)(item?.target?.value)}
          label={label}
          error={Boolean(error)}
          getOptions={getOptions}
          parseOption={(value) => ({
            text: value.name,
            value: value.id,
          })}
          helperText={helperText}
          value={values[name]}
          {...(disabled && { disabled: disabled(values) })}
        />
      </Box>
    ) : name === 'customerType' ? (
      <AutocompleteComplex
        key={name}
        label={label}
        value={values[name]}
        name={name}
        onChange={handleChange(name)}
        onBlur={onBlur}
        InputProps={{ onPaste: handleChange(name) }}
        inputProps={{
          onKeyDown: (e) => e.preventDefault(),
          style: styles.transparentCaret,
        }}
        error={error}
        helperText={helperText}
        items={CUSTOMER_TYPES}
        parseOption={({ value, label }) => ({ value, label })}
        isOptionEqualToValue={(option, value) => option?.value === value?.value}
        sx={styles.inputAutocompleteCustomerType(responsive)}
        size='medium'
        placeholder={null}
      />
    ) : type === 'AutocompleteComplexFetch' ? (
      <AutocompleteComplexFetch
        key={name}
        label={label}
        name={name}
        value={values[name]}
        onChange={handleChange(name)}
        onBlur={onBlur}
        InputProps={{ onPaste: handleChange(name) }}
        error={error}
        helperText={helperText}
        getOptions={name === 'city' ? () => getOptions(values['province']) : getOptions}
        parseOption={parseOption}
        freeSolo={true}
        {...(disabled && { disabled: disabled(values) })}
        sx={styles.inputAutocomplete(responsive)}
        size='medium'
      />
    ) : (
      <AutocompleteComplex
        key={name}
        label={label}
        value={values[name]}
        name={name}
        onChange={handleChange(name)}
        onBlur={onBlur}
        InputProps={{ onPaste: handleChange(name) }}
        error={error}
        helperText={helperText}
        items={data}
        parseOption={({ id, name }) => ({ id: id, title: name })}
        freeSolo={true}
        disabled={isLoadingCustomerGroups || groupsError}
        sx={styles.inputAutocomplete(responsive)}
        size='medium'
      />
    );
  });
};
export default AddClient;
FormFields.propTypes = {
  fields: PropTypes.array,
  values: PropTypes.array,
  errors: PropTypes.array,
  handleChange: PropTypes.func,
  onBlur: PropTypes.func,
  responsive: PropTypes.bool,
  data: PropTypes.array,
  isLoadingCustomerGroups: PropTypes.bool,
  groupsError: PropTypes.bool,
};
AddClient.propTypes = {
  isOpen: PropTypes.bool,
  isLoading: PropTypes.bool,
  onClose: PropTypes.func,
  params: PropTypes.object,
  retry: PropTypes.func,
  onFiltersFormSubmit: PropTypes.func,
};
